import React, { useState, useEffect } from 'react';
import { Modal, Spin, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { updateSprint, createSprint } from '../api/projectManagementApi';
import { SPRINT_DURATIONS, SPRINT_START_DAYS } from './constants';

export default function SprintSettingsModal({ open, onClose, selectedSprint, onSaved }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: '',
        durationWeeks: 2,
        startDay: 'Monday',
        // calendar exclusions
        companyHolidays: true,
        publicHolidays: true,
        companyEvents: false,
        // AI planning
        carryForward: true,
        autoReport: true,
        workloadBalancing: true,
        deadlinePrediction: true,
        blockerDetection: true,
    });

    // ── Pre-populate from current sprint whenever modal opens ─────────────────
    useEffect(() => {
        if (!open) return;
        if (selectedSprint) {
            setForm({
                name: selectedSprint.name || '',
                durationWeeks: selectedSprint.durationWeeks ?? 2,
                startDay: selectedSprint.startDay ?? 'Monday',
                companyHolidays: selectedSprint.calendarExclusions?.companyHolidays ?? true,
                publicHolidays: selectedSprint.calendarExclusions?.publicHolidays ?? true,
                companyEvents: selectedSprint.calendarExclusions?.companyEvents ?? false,
                carryForward: selectedSprint.settings?.carryForward ?? true,
                autoReport: selectedSprint.settings?.autoReport ?? true,
                workloadBalancing: selectedSprint.settings?.workloadBalancing ?? true,
                deadlinePrediction: selectedSprint.settings?.deadlinePrediction ?? true,
                blockerDetection: selectedSprint.settings?.blockerDetection ?? true,
            });
        } else {
            // No sprint yet — reset to defaults
            setForm({
                name: '',
                durationWeeks: 2,
                startDay: 'Monday',
                companyHolidays: true,
                publicHolidays: true,
                companyEvents: false,
                carryForward: true,
                autoReport: true,
                workloadBalancing: true,
                deadlinePrediction: true,
                blockerDetection: true,
            });
        }
    }, [open, selectedSprint]);

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const handleSave = async () => {
        if (!form.name.trim()) {
            message.warning('Sprint name is required.');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                durationWeeks: form.durationWeeks,
                startDay: form.startDay,
                calendarExclusions: {
                    companyHolidays: form.companyHolidays,
                    publicHolidays: form.publicHolidays,
                    companyEvents: form.companyEvents,
                },
                settings: {
                    carryForward: form.carryForward,
                    autoReport: form.autoReport,
                    workloadBalancing: form.workloadBalancing,
                    deadlinePrediction: form.deadlinePrediction,
                    blockerDetection: form.blockerDetection,
                },
            };

            if (selectedSprint?._id) {
                await updateSprint(selectedSprint._id, payload);
                message.success('Sprint settings saved.');
            } else {
                // Create a new sprint if none exists
                payload.status = 'planned';
                await createSprint(payload);
                message.success('Sprint created successfully.');
            }

            onSaved?.();
            onClose();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to save sprint settings.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            title={
                <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-[#003263]" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18 }}>settings</span>
                    <span>Sprint Settings</span>
                </div>
            }
            width={600}
            styles={{ body: { padding: '24px 0' } }}
        >
            {selectedSprint && selectedSprint.status !== 'planned' && (
                <div className="mx-6 mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-2.5">
                    <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 mt-0.5" style={{ fontSize: 18, fontFamily: 'Material Symbols Outlined' }}>info</span>
                    <span className="text-xs text-blue-800 dark:text-blue-300 leading-normal font-medium">
                        This sprint has already started. Any updates to duration, start day, calendar exclusions, or AI planning options will be applied only to newly created future sprints to preserve active sprint configurations.
                    </span>
                </div>
            )}
            <div className="px-6 space-y-6 max-h-[60vh] overflow-y-auto">

                {/* Sprint Name */}
                <div>
                    <h3 className="font-semibold text-sm mb-3 text-[#191c1d] dark:text-white">Sprint Name</h3>
                    <input
                        type="text"
                        className="w-full border border-[#e7e8e9] dark:border-slate-600 rounded-lg p-2 text-sm bg-white dark:bg-[#1e293b] text-[#191c1d] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#03488A]"
                        placeholder="e.g. Sprint 15"
                        value={form.name}
                        onChange={e => set('name', e.target.value)}
                    />
                </div>

                {/* General Settings */}
                <div>
                    <h3 className="font-semibold text-sm mb-3 text-[#191c1d] dark:text-white">General Settings</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs text-[#737782] block mb-1">Sprint Duration</label>
                            <select
                                className="w-full border border-[#e7e8e9] dark:border-slate-600 rounded-lg p-2 text-sm bg-white dark:bg-[#1e293b] text-[#191c1d] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#03488A]"
                                value={form.durationWeeks}
                                onChange={e => set('durationWeeks', Number(e.target.value))}
                            >
                                {SPRINT_DURATIONS.map(d => (
                                    <option key={d.value} value={d.value}>{d.label}{d.value === 2 ? ' (Default)' : ''}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-[#737782] block mb-1">Sprint Start Day</label>
                            <select
                                className="w-full border border-[#e7e8e9] dark:border-slate-600 rounded-lg p-2 text-sm bg-white dark:bg-[#1e293b] text-[#191c1d] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#03488A]"
                                value={form.startDay}
                                onChange={e => set('startDay', e.target.value)}
                            >
                                {SPRINT_START_DAYS.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Calendar Exclusions */}
                <div>
                    <h3 className="font-semibold text-sm mb-3 text-[#191c1d] dark:text-white">Calendar Settings (Exclusions)</h3>
                    <div className="space-y-2.5">
                        {[
                            { key: 'companyHolidays', label: 'Company Holidays' },
                            { key: 'publicHolidays', label: 'Public Holidays' },
                            { key: 'companyEvents', label: 'Company Events' },
                        ].map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-2.5 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-[#03488A] cursor-pointer"
                                    checked={form[key]}
                                    onChange={e => set(key, e.target.checked)}
                                />
                                <span className="text-[#424750] dark:text-slate-300">{label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* AI Planning Options */}
                <div>
                    <h3 className="font-semibold text-sm mb-3 text-[#191c1d] dark:text-white">AI Planning Options</h3>
                    <div className="space-y-2.5">
                        {[
                            { key: 'carryForward', label: 'Automatically Carry Forward Incomplete Tasks' },
                            { key: 'autoReport', label: 'Auto Generate Sprint Reports' },
                            { key: 'workloadBalancing', label: 'Enable AI Workload Balancing Suggestions' },
                            { key: 'deadlinePrediction', label: 'Enable Deadline Prediction' },
                            { key: 'blockerDetection', label: 'Enable Blocker Detection' },
                        ].map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-2.5 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-[#03488A] cursor-pointer"
                                    checked={form[key]}
                                    onChange={e => set(key, e.target.checked)}
                                />
                                <span className="text-[#424750] dark:text-slate-300">{label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="px-6 pt-4 border-t border-[#e7e8e9] dark:border-slate-700 flex justify-end gap-2 mt-4">
                <button
                    onClick={onClose}
                    disabled={saving}
                    className="px-4 py-2 border border-[#e7e8e9] dark:border-slate-600 rounded-lg text-sm font-semibold text-[#424750] dark:text-slate-300 hover:bg-[#f3f4f5] dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-[#003263] text-white rounded-lg text-sm font-semibold hover:bg-[#03488A] disabled:opacity-60 transition-colors flex items-center gap-2"
                >
                    {saving && <Spin indicator={<LoadingOutlined style={{ fontSize: 14, color: '#fff' }} />} />}
                    {selectedSprint?._id ? 'Save Settings' : 'Create Sprint'}
                </button>
            </div>
        </Modal>
    );
}
